import React from 'react'
import {
    Box,
    Button,
    Chip,
    InputAdornment,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import SearchIcon from '@material-ui/icons/Search'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    row: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    search: {
        flex: '1 1 220px',
        minWidth: 220,
    },
    count: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
    },
    countValue: {
        fontWeight: 700,
        fontFamily: 'monospace',
        color: theme.palette.text.primary,
    },
    searchHint: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
    },
    statusFilters: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(0.75),
    },
    statusLabel: {
        fontSize: '0.8125rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(0.5),
    },
}))

export type ApplicationStatusFilter =
    | 'all'
    | 'in_progress'
    | 'submitted'
    | 'pending_review'

export type ScreeningStatusFilter =
    | 'all'
    | 'invited'
    | 'approved'
    | 'consider'
    | 'declined'

const APPLICATION_STATUS_FILTERS: {
    value: ApplicationStatusFilter
    label: string
}[] = [
    { value: 'all', label: 'All' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'pending_review', label: 'Pending review' },
]

function StatusFilterRow<T extends string>({
    items,
    active,
    onChange,
    labelClass,
    rowClass,
}: {
    items: { value: T; label: string }[]
    active: T
    onChange: (value: T) => void
    labelClass: string
    rowClass: string
}) {
    return (
        <Box className={rowClass}>
            <Typography className={labelClass}>Status</Typography>
            {items.map((item) => {
                const selected = active === item.value
                return (
                    <Chip
                        key={item.value}
                        size="small"
                        label={item.label}
                        color={selected ? 'primary' : 'default'}
                        variant={selected ? 'default' : 'outlined'}
                        onClick={() => onChange(item.value)}
                    />
                )
            })}
        </Box>
    )
}

const SCREENING_STATUS_FILTERS: {
    value: ScreeningStatusFilter
    label: string
}[] = [
    { value: 'all', label: 'All' },
    { value: 'invited', label: 'Invited' },
    { value: 'approved', label: 'Approved' },
    { value: 'consider', label: 'Consider' },
    { value: 'declined', label: 'Declined' },
]

interface PipelineToolbarProps {
    query: string
    onQueryChange: (value: string) => void
    rowCount: number
    searchSummary?: string | null
    showAddLead: boolean
    onAddLead?: () => void
    applicationStatusFilter?: ApplicationStatusFilter
    onApplicationStatusFilterChange?: (value: ApplicationStatusFilter) => void
    screeningStatusFilter?: ScreeningStatusFilter
    onScreeningStatusFilterChange?: (value: ScreeningStatusFilter) => void
}

export const PipelineToolbar: React.FC<PipelineToolbarProps> = ({
    query,
    onQueryChange,
    rowCount,
    searchSummary,
    showAddLead,
    onAddLead,
    applicationStatusFilter,
    onApplicationStatusFilterChange,
    screeningStatusFilter,
    onScreeningStatusFilterChange,
}) => {
    const classes = useStyles()
    const applicationFilters =
        applicationStatusFilter != null &&
        onApplicationStatusFilterChange != null
            ? {
                  value: applicationStatusFilter,
                  onChange: onApplicationStatusFilterChange,
              }
            : null
    const screeningFilters =
        applicationFilters == null &&
        screeningStatusFilter != null &&
        onScreeningStatusFilterChange != null
            ? {
                  value: screeningStatusFilter,
                  onChange: onScreeningStatusFilterChange,
              }
            : null

    return (
        <Box className={classes.root}>
            <Box className={classes.row}>
                <TextField
                    className={classes.search}
                    size="small"
                    variant="outlined"
                    placeholder="Search by name or email"
                    value={query}
                    onChange={(e) => onQueryChange(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="disabled" />
                            </InputAdornment>
                        ),
                    }}
                />

                {searchSummary && (
                    <Typography className={classes.searchHint}>
                        {searchSummary}
                    </Typography>
                )}

                <Typography className={classes.count}>
                    <span className={classes.countValue}>{rowCount}</span>{' '}
                    people
                </Typography>

                {showAddLead && onAddLead && (
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={onAddLead}
                        style={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Add lead
                    </Button>
                )}
            </Box>

            {applicationFilters && (
                <StatusFilterRow
                    items={APPLICATION_STATUS_FILTERS}
                    active={applicationFilters.value}
                    onChange={applicationFilters.onChange}
                    labelClass={classes.statusLabel}
                    rowClass={classes.statusFilters}
                />
            )}
            {screeningFilters && (
                <StatusFilterRow
                    items={SCREENING_STATUS_FILTERS}
                    active={screeningFilters.value}
                    onChange={screeningFilters.onChange}
                    labelClass={classes.statusLabel}
                    rowClass={classes.statusFilters}
                />
            )}
        </Box>
    )
}
